import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Image,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Tooltip,
  IconButton,
  VStack,
} from "@chakra-ui/react";
import { HiOutlinePlus } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodifyDefinitionsGrid from "./components/CodifyDefinitionsGrid";
import CodifySpecificationsGrid from "./components/CodifySpecificationsGrid.jsx";
import CollectionCards from "./components/CollectionCards";
import DefinitionsFilterBar from "./components/DefinitionsFilterBar";
import Codify from "./Codify";
import { useAuth } from "./AuthContext";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";

const CodifyHomepage = () => {
  const navigate = useNavigate();
  const { get } = useAuthenticatedRequest();

  const [codifyDefinitions, setCodifyDefinitions] = useState([]);
  const [codifyDefinitionsLoading, setCodifyDefinitionsLoading] = useState(true);

  // Filter states for definitions
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchText, setSearchText] = useState("");

  const handleCodifyDefinitionCopy = (newDefinition) => {
    setCodifyDefinitions((prev) => [...prev, newDefinition]);
  };

  // Clear type filter when collection changes
  const handleCollectionSelect = (collection) => {
    setSelectedCollection(collection);
    setSelectedType(null); // Reset type filter when collection changes
  };

  const handleClearFilters = () => {
    setSelectedCollection(null);
    setSelectedType(null);
    setSearchText("");
  };

  // Filter definitions based on selected filters
  const filteredDefinitions = useMemo(() => {
    let filtered = codifyDefinitions;

    // Filter by collection
    if (selectedCollection) {
      filtered = filtered.filter((def) => def.collection === selectedCollection);
    }

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter((def) => def.type === selectedType);
    }

    // Filter by search text
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (def) =>
          def.type?.toLowerCase().includes(search) ||
          def.description?.toLowerCase().includes(search) ||
          def.collection?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [codifyDefinitions, selectedCollection, selectedType, searchText]);

  const [codifySpecifications, setCodifySpecifications] = useState([]);
  const [codifySpecificationsLoading, setCodifySpecificationsLoading] = useState(true);

  const handleCodifySpecificationCopy = (newSpecification) => {
    setCodifySpecifications((prev) => [...prev, newSpecification]);
  };

  useEffect(() => {
    const fetchCodifyDefinitions = async () => {
      try {
        const response = await get("https://codify.poc.mrc.ai/api/definitions");
        setCodifyDefinitions(response.data);
      } catch (err) {
        console.error("Failed to fetch codify definitions:", err);
      } finally {
        setCodifyDefinitionsLoading(false);
      }
    };

    const fetchCodifySpecifications = async () => {
      try {
        const response = await get("https://codify.poc.mrc.ai/api/specifications");
        setCodifySpecifications(response.data);
      } catch (err) {
        console.error("Failed to fetch codify specifications:", err);
      } finally {
        setCodifySpecificationsLoading(false);
      }
    };

    fetchCodifyDefinitions();
    fetchCodifySpecifications();
  }, []);

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <Navbar />
      </Box>

      {/* Watermark background */}
      <Box
        position="relative"
        width="100%"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        {/* Overlay content */}
        <Box position="relative" zIndex={1} p={1} display="flex" flexDirection="column" height="100%">
          <Tabs
            orientation="horizontal"
            variant="line"
            colorScheme="linkedin"
            isLazy={true}
            lazyBehavior="unmount"
            display="flex"
            flexDirection="column"
            height="100%">
            <TabList>
              <Tab>Definitions</Tab>
              <Tab>Specifications</Tab>
              <Tab>Test</Tab>
            </TabList>
            <TabPanels flex="1" overflow="auto">
              {/* Codify Definitions */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <VStack spacing={3} align="stretch" height="100%">
                  {/* Action bar */}
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800"
                    flexShrink={0}>
                    <HStack>
                      <Tooltip label="Add Codify Definition">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add Codify Definition"
                          size="md"
                          onClick={() => navigate("/codifydefinitions/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {/* Collection Cards */}
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={3}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800"
                    flexShrink={0}>
                    <CollectionCards
                      definitions={codifyDefinitions}
                      selectedCollection={selectedCollection}
                      onCollectionSelect={handleCollectionSelect}
                    />
                  </Box>

                  {/* Filter Bar */}
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={3}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800"
                    flexShrink={0}>
                    <DefinitionsFilterBar
                      definitions={codifyDefinitions}
                      selectedCollection={selectedCollection}
                      selectedType={selectedType}
                      searchText={searchText}
                      onTypeChange={setSelectedType}
                      onSearchChange={setSearchText}
                      onClearFilters={handleClearFilters}
                    />
                  </Box>

                  {/* Definitions Grid */}
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={4}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800"
                    flex="1"
                    display="flex"
                    flexDirection="column"
                    overflow="hidden">
                    <CodifyDefinitionsGrid
                      codifyDefinitions={filteredDefinitions}
                      loading={codifyDefinitionsLoading}
                      onCopy={handleCodifyDefinitionCopy}
                      selectedCollection={selectedCollection}
                    />
                  </Box>
                </VStack>
              </TabPanel>

              {/* Codify Specifications */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  mb={2}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flexShrink={0}>
                  <HStack>
                    <Tooltip label="Add Codify Specification">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        aria-label="Add Codify Specification"
                        size="md"
                        onClick={() => navigate("/codifyspecifications/new")}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden">
                  <CodifySpecificationsGrid
                    codifySpecifications={codifySpecifications}
                    loading={codifySpecificationsLoading}
                    onCopy={handleCodifySpecificationCopy}
                  />
                </Box>
              </TabPanel>

              {/* Codify Test Tool */}
              <TabPanel height="100%" p={0} overflow="hidden">
                <Box height="100%" overflow="auto">
                  <Codify embedded={true} />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Box>
  );
};

export default CodifyHomepage;
